import { graphql } from 'app/generated/gql'
import { sortBy } from 'lodash'

import type { ContactJson } from 'app/types/entities'
import type { MessageJson } from 'app/types/entities'
import { gqlFetchList, gqlQuery } from 'app/lib/graphqlClient'
import { optOutFor } from 'app/lib/smsOptOut'
import { phonePairKey } from 'app/lib/smsThreadId'
import { isUnreadSuppressed } from 'app/lib/unreadOptOuts'
import { MESSAGE_FIELDS, OPT_OUT_KEYS_PER_QUERY } from 'app/lib/inboxTypes'
import { excludeHeld } from 'app/lib/heldMessages'
import type { ConversationType, InboxState, ResolvedInboxSelection } from 'app/lib/inboxTypes'

// `searchClause` is resolved phone numbers, not the raw query: the messages index has no name field.
export function buildConversationListSearch(locationId: string, filter: string, searchClause?: string): string {
  const parts: string[] = [`locationId:"${locationId}"`]
  // archived filter needs fetchMessagesPage's `archived: true` arg; resolver excludes archived by default (TRK-5533).
  if (filter === `archived`) parts.push(`archived:true`)
  else parts.push(`NOT archived:true`)
  // Exclude outbound (user/AI) messages so their read:false replies don't leak into Unread.
  if (filter === `unread`) parts.push(`NOT read:true AND NOT _exists_:sentByUserId AND NOT isAgent:true`)
  // Inbound only, so every conversation aggregated from this page has a message the contact sent.
  if (filter === `replied`) parts.push(`NOT _exists_:sentByUserId AND NOT isAgent:true`)
  if (filter === `ai_agent`) parts.push(`isAgent:true`)
  if (searchClause) parts.push(searchClause)
  return excludeHeld(parts.join(` AND `))
}

// Query opted-out threads by phone (quiet threads fall out of the 500-message page).
// WARNING: the index matches the bare last-10-digits form only; +1-prefixed forms return nothing.
export function buildOptedOutSearches(locationId: string, optOutKeys: string[], searchClause?: string): string[] {
  if (optOutKeys.length === 0) return []
  const searches: string[] = []
  for (let index = 0; index < optOutKeys.length; index += OPT_OUT_KEYS_PER_QUERY) {
    const chunk = optOutKeys.slice(index, index + OPT_OUT_KEYS_PER_QUERY)
    const phoneClauses = chunk.flatMap((key) => [`from:"${key}"`, `to:"${key}"`])
    const parts: string[] = [`locationId:"${locationId}"`, `NOT archived:true`, `(${phoneClauses.join(` OR `)})`]
    if (searchClause) parts.push(searchClause)
    searches.push(parts.join(` AND `))
  }
  return searches
}

// Split conversations on opt-out state (derived at read time). Exempt: archived filter and the open thread.
export function partitionByOptOut(
  conversations: ConversationType[],
  optOuts: ReadonlyMap<string, string>,
  filter: string,
  selectedThreadId: string,
): ConversationType[] {
  if (filter === `archived`) return conversations
  const wantSuppressed = filter === `opted_out`
  // Empty map: no-op for working-list filters, but an empty list for `opted_out`.
  if (optOuts.size === 0) return wantSuppressed ? [] : conversations
  return conversations.filter((conversation) => {
    if (conversation.threadId && conversation.threadId === selectedThreadId) return true
    return !!optOutFor(conversationTextMessage(conversation), optOuts) === wantSuppressed
  })
}

// Hand-written: gqlFetchList cannot pass the `archived` resolver argument.
const MESSAGES_WITH_ARCHIVED_QUERY = graphql(`
  query InboxMessagesWithArchived($search: String, $pagination: PaginationInput, $sort: SortInput) {
    messages(archived: true, search: $search, pagination: $pagination, sort: $sort) {
      hits {
        id
        orgId
        workspaceId
        locationId
        threadId
        from
        to
        callerId
        sentByUserId
        isAgent
        aiWorkflowId
        aiWorkflowName
        isInitialMessage
        contactId
        channel
        subject
        htmlBody
        attachments {
          filename
          contentType
          sizeBytes
        }
        text
        read
        archived
        createdDate
        updatedDate
        status
        providerMessageId
        errorCode
        errorMessage
      }
      total
      page
    }
  }
`)

export async function fetchMessagesPage(
  search: string,
  sortDirection: `ASC` | `DESC`,
  limit = 500,
  includeArchived = false,
): Promise<MessageJson[]> {
  // network-only: powers the poll and post-mutation refreshes; cache-first would hide new messages.
  if (includeArchived) {
    const result = await gqlQuery<{ messages: { hits: MessageJson[] } }>(
      MESSAGES_WITH_ARCHIVED_QUERY,
      { search, pagination: { limit }, sort: { field: `createdDate`, direction: sortDirection } },
      { requestPolicy: `network-only` },
    )
    return result.data?.messages?.hits ?? []
  }
  const result = await gqlFetchList<MessageJson>(
    `messages`,
    { search, pagination: { limit }, sort: { field: `createdDate`, direction: sortDirection } },
    MESSAGE_FIELDS,
    { requestPolicy: `network-only` },
  )
  return result.hits
}

// Merge per-chunk pages, dedupe by id, restore newest-first (aggregateConversations reads first-seen as latest).
export function mergeMessagePages(pages: MessageJson[][]): MessageJson[] {
  const seenIds = new Set<string>()
  const merged: MessageJson[] = []
  for (const page of pages) {
    for (const message of page) {
      if (message.id) {
        if (seenIds.has(message.id)) continue
        seenIds.add(message.id)
      }
      merged.push(message)
    }
  }
  return merged.sort((left, right) => {
    const leftTime = left.createdDate ? new Date(left.createdDate).getTime() : 0
    const rightTime = right.createdDate ? new Date(right.createdDate).getTime() : 0
    return rightTime - leftTime
  })
}

export function aggregateConversations(
  conversationMessages: MessageJson[],
  unreadByThread: Record<string, number>,
): ConversationType[] {
  // Group by phone pair, not raw threadId: a thread-id migration (TRK-5414) can split one
  // conversation across two threadIds. Fall back to threadId when a phone is missing.
  // An email has no phone pair, so it joins its contact's latest text conversation.
  const groupKeyByThread = new Map<string, string>()
  const textGroupByContact = new Map<string, string>()
  for (const message of conversationMessages) {
    if (!message.threadId || message.channel === `email`) continue
    let groupKey = groupKeyByThread.get(message.threadId)
    if (!groupKey) {
      groupKey = phonePairKey(message.from, message.to) ?? message.threadId
      groupKeyByThread.set(message.threadId, groupKey)
    }
    if (message.contactId && !textGroupByContact.has(message.contactId))
      textGroupByContact.set(message.contactId, groupKey)
  }
  const latestByGroup = new Map<string, MessageJson>()
  const latestTextByGroup = new Map<string, MessageJson>()
  const totalByGroup = new Map<string, number>()
  const hasAIByGroup = new Set<string>()
  const threadIdsByGroup = new Map<string, Set<string>>()
  for (const message of conversationMessages) {
    if (!message.threadId) continue
    const contactGroupKey = message.contactId
      ? (textGroupByContact.get(message.contactId) ?? `contact:${message.contactId}`)
      : undefined
    const groupKey =
      message.channel === `email`
        ? (contactGroupKey ?? groupKeyByThread.get(message.threadId) ?? message.threadId)
        : groupKeyByThread.get(message.threadId)!
    if (!latestByGroup.has(groupKey)) latestByGroup.set(groupKey, message)
    if (message.channel !== `email` && !latestTextByGroup.has(groupKey)) latestTextByGroup.set(groupKey, message)
    totalByGroup.set(groupKey, (totalByGroup.get(groupKey) ?? 0) + 1)
    if (message.isAgent) hasAIByGroup.add(groupKey)
    const memberThreadIds = threadIdsByGroup.get(groupKey) ?? new Set<string>()
    memberThreadIds.add(message.threadId)
    threadIdsByGroup.set(groupKey, memberThreadIds)
  }
  const rows: ConversationType[] = []
  latestByGroup.forEach((latest, groupKey) => {
    const memberThreadIds = [...(threadIdsByGroup.get(groupKey) ?? [])]
    const unread = memberThreadIds.reduce((sum, threadId) => sum + (unreadByThread[threadId] ?? 0), 0)
    const latestText = latestTextByGroup.get(groupKey)
    rows.push({
      ...latest,
      // The text thread anchors selection, archive and mark-unread; an email thread holds only outbound mail.
      threadId: latestText?.threadId ?? latest.threadId,
      latestText,
      total: totalByGroup.get(groupKey) ?? 1,
      unread,
      hasAI: hasAIByGroup.has(groupKey),
      memberThreadIds,
    } as ConversationType)
  })
  return rows
}

export function aggregateUnreads(
  unreadMessages: MessageJson[],
  currentLocationId: string,
  suppression: ReadonlySet<string> = new Set(),
): { byLocation: Record<string, number>; byThread: Record<string, number> } {
  const threadsByLocation: Record<string, Set<string>> = {}
  const byThread: Record<string, number> = {}
  for (const message of unreadMessages) {
    // Only inbound messages count as unread; user/AI messages are outbound.
    if (message.sentByUserId || message.isAgent) continue
    // byLocation drives picker badges (suppresses opted-out); byThread counts them for the Opted out list.
    // Picker badges count threads, like the sidebar badge (`countUnreadThreads`), so the two agree.
    if (message.locationId && message.threadId && !isUnreadSuppressed(message, suppression))
      (threadsByLocation[message.locationId] ??= new Set()).add(message.threadId)
    if (message.locationId === currentLocationId && message.threadId) {
      byThread[message.threadId] = (byThread[message.threadId] ?? 0) + 1
    }
  }
  const byLocation = Object.fromEntries(
    Object.entries(threadsByLocation).map(([locationId, threadIds]) => [locationId, threadIds.size]),
  )
  return { byLocation, byThread }
}

export function sortConversationsChronologically(conversations?: ConversationType[]) {
  return sortBy(conversations ?? [], `createdDate`).reverse()
}

export function matchesSelectedConversation(
  conversation: ConversationType,
  selectedThreadId: string,
  selectedContactId?: string,
  fallbackThreadId?: string,
) {
  if (selectedContactId) return conversation.contactId === selectedContactId
  const threadToMatch = fallbackThreadId ?? selectedThreadId
  if (!threadToMatch) return false
  return conversation.threadId === threadToMatch
}

export function getActiveConversation(state: InboxState, fallbackThreadId?: string) {
  return state.conversations.find((conversation) =>
    matchesSelectedConversation(conversation, state.selectedThreadId, state.selectedContactId, fallbackThreadId),
  )
}

// The message whose from/to carry the contact's phone. An email's from/to are addresses, never a phone.
export function conversationTextMessage(conversation: ConversationType): MessageJson | undefined {
  return conversation.channel === `email` ? conversation.latestText : conversation
}

export function getConversationPhone(conversation?: ConversationType) {
  const message = conversation && conversationTextMessage(conversation)
  if (!message) return undefined
  return message.sentByUserId || message.isAgent ? message.to : message.from
}

export function createInitialInboxState(threadId: string): InboxState {
  return {
    isLoading: false,
    hasSynced: false,
    pendingMutationCount: 0,
    selectedThreadId: threadId,
    selectedContactId: undefined,
    selectedContact: undefined,
    conversations: [],
    messages: undefined,
    unreadLocations: undefined,
    optimisticMessages: undefined,
    optimisticUnreadThreads: {},
  }
}

export function sortMessagesChronologically(messages?: MessageJson[]) {
  if (messages == null) return messages

  return [...messages].sort((left, right) => {
    const leftTime = left.createdDate ? new Date(left.createdDate).getTime() : 0
    const rightTime = right.createdDate ? new Date(right.createdDate).getTime() : 0

    if (leftTime !== rightTime) return leftTime - rightTime
    return (left.id ?? ``).localeCompare(right.id ?? ``)
  })
}

export function hasCanonicalCaughtUp(canonicalMessages: MessageJson[], optimisticMessages?: MessageJson[]) {
  if (!optimisticMessages || optimisticMessages.length === 0) return true

  const canonicalIds = new Set(canonicalMessages.map((message) => message.id).filter((id): id is string => Boolean(id)))

  const outgoingCaughtUp = optimisticMessages
    .filter((message) => message.sentByUserId && message.id)
    .every((message) => canonicalIds.has(message.id!))

  if (!outgoingCaughtUp) return false

  const canonicalReadMap = new Map(canonicalMessages.filter((m) => m.id).map((m) => [m.id!, m.read]))

  // Hold optimistic state until canonical reflects each user-touched read flag (both directions).
  const readStatusCaughtUp = optimisticMessages
    .filter((message) => !message.sentByUserId && !message.isAgent && message.id)
    .every((message) => canonicalReadMap.get(message.id!) === message.read)

  return readStatusCaughtUp
}

export function mergeConversationsPreservingOptimisticUnread(
  stateConversations: ConversationType[],
  canonicalConversations: ConversationType[],
  selectedThreadId: string,
  selectedContactId?: string,
): ConversationType[] {
  const stateActive = stateConversations.find((c) =>
    matchesSelectedConversation(c, selectedThreadId, selectedContactId),
  )
  if (!stateActive) return canonicalConversations

  return canonicalConversations.map((c) => {
    if (!matchesSelectedConversation(c, selectedThreadId, selectedContactId)) return c
    return { ...c, unread: stateActive.unread, read: stateActive.read }
  })
}

// Every identity a conversation can be matched by, so a pending row is absorbed once the real thread lands.
function conversationIdentities(conversation: ConversationType): string[] {
  return [conversation.threadId, conversation.contactId, getConversationPhone(conversation)].filter(
    (identity): identity is string => Boolean(identity),
  )
}

export function mergePlaceholderConversations(current: ConversationType[], canonical: ConversationType[]) {
  const canonicalIdentities = new Set(canonical.flatMap(conversationIdentities))
  const placeholderConversations = current.filter((conversation) => {
    if ((conversation.total ?? 0) !== 0) return false
    const identities = conversationIdentities(conversation)
    return identities.length > 0 && !identities.some((identity) => canonicalIdentities.has(identity))
  })

  return sortConversationsChronologically(canonical.concat(placeholderConversations))
}

// Deliberately no "auto-open most recent" fallback: only the route, contact, or URL tab select a thread.
export function resolveInboxSelection({
  conversations,
  threadId,
  contactId,
  tabThreadId,
  hasMessages,
  hasSelectedContact,
  hasOptimisticMessages,
  selectedContact,
}: {
  conversations: ConversationType[]
  threadId: string
  contactId?: string
  tabThreadId?: string | null
  hasMessages: boolean
  hasSelectedContact: boolean
  hasOptimisticMessages: boolean
  selectedContact?: ContactJson
}): ResolvedInboxSelection {
  const conversationByThread = threadId
    ? conversations.find((conversation) => conversation.threadId === threadId)
    : undefined

  const conversationByContact =
    !conversationByThread && contactId
      ? conversations.find((conversation) => conversation.contactId === contactId)
      : undefined

  const conversation =
    conversationByThread ??
    conversationByContact ??
    conversations.find((candidate) => candidate.threadId === tabThreadId)

  if (conversation) {
    const phone = getConversationPhone(conversation)
    const nextThreadId = !threadId && conversation.threadId ? conversation.threadId : threadId
    const nextContactId = conversation.contactId !== contactId ? conversation.contactId : contactId
    const shouldRefreshSelectedContact =
      !selectedContact ||
      selectedContact.id !== conversation.contactId ||
      (phone != null && selectedContact.cellPhone !== phone)
    const nextSelectedContact = shouldRefreshSelectedContact
      ? ({
          ...(conversation.contactId ? { id: conversation.contactId } : {}),
          ...(phone ? { cellPhone: phone } : {}),
        } as ContactJson)
      : selectedContact

    return {
      type: `select`,
      threadId: nextThreadId ?? ``,
      contactId: nextContactId,
      selectedContact: nextSelectedContact,
    }
  }

  return (!threadId || !hasMessages) &&
    (Boolean(threadId) || Boolean(contactId) || hasSelectedContact || hasOptimisticMessages)
    ? { type: `clear` }
    : { type: `noop` }
}
